import {
  Box,
  ButtonProps,
  CustomModalLayoutProps,
  TextButton,
  Text,
} from '@wix/design-system';
import { ExportState, FiltersMap } from '@wix/bex-core';
import React, { ReactElement } from 'react';
import { isErrorDetailsHttpError, isHttpError } from '@wix/bex-core/util';
import { ApplicationError } from '@wix/bex-utils/@wix/ambassador-fedinfra-exportservice-v1-export-async-job/types';

export type RenderModalError = (
  data: ApplicationError['data'],
) => undefined | Partial<CustomModalLayoutProps & { children?: ReactElement }>;

export function getExportModalErrorProps<T, F extends FiltersMap>({
  renderError,
  state,
}: {
  state: ExportState<T, F>;
  renderError?: RenderModalError;
}): Partial<CustomModalLayoutProps & { children?: ReactElement }> {
  const {
    exportStatus: { error },
    translate: t,
  } = state;

  const details = isErrorDetailsHttpError(error) ? error.response.data : null;
  const applicationErrorCode = details?.applicationError?.code;
  const applicationErrorData = details?.applicationError?.data;

  if (!isHttpError(error)) {
    return {};
  }

  const statusCode = error.response?.status;

  if (statusCode === 401) {
    return {
      primaryButtonText: t('Refresh' as any),
      primaryButtonOnClick: () => {
        window.location.reload();
      },
      children: <Text widows>{t('cairo.export.sessionExpiredMessage')}</Text>,
    };
  }

  if (
    statusCode === 403 ||
    applicationErrorCode === 'method_endpoint_permission_denied'
  ) {
    return {
      primaryButtonText: t('Got It' as any),
      primaryButtonOnClick: state.onFailedClose,
      children: (
        <Box direction="vertical">
          <Text widows>{t('cairo.export.noPermissionsMessage')}</Text>
          <TextButton
            as="a"
            href="https://support.wix.com/en/article/roles-permissions-overview"
            target="_blank"
          >
            {t('cairo.export.noPermissionsCTA')}
          </TextButton>
        </Box>
      ),
    };
  }

  if (
    applicationErrorCode === 'media_generate_signed_url' ||
    applicationErrorCode === 'media_appender_completion' ||
    applicationErrorCode === 'async_job_infra'
  ) {
    return {
      primaryButtonText: t('cairo.error.contactSupport.cta'),
      primaryButtonProps: {
        as: 'a',
        href: 'https://www.wix.com/contact',
        target: '_blank',
      } as ButtonProps,
      children: <Text widows>{t('cairo.export.databaseIssueMessage')}</Text>,
    };
  }

  if (applicationErrorCode === 'max_polling_stale_job') {
    return {
      primaryButtonText: t('cairo.export.tryAgain'),
      primaryButtonOnClick: state.onRetryExportToClick,
      children: <Text widows>{t('cairo.export.overloadErrorMessage')}</Text>,
    };
  }

  if (applicationErrorCode === 'offline') {
    return {
      title: t('cairo.connection.error.title'),
      primaryButtonText: null,
      primaryButtonOnClick: undefined,
      children: <Text widows>{t('cairo.export.offlineMessage')}</Text>,
    };
  }

  if (applicationErrorCode === 'grpc_query_target_internal') {
    return {
      primaryButtonText: t('cairo.error.contactSupport.cta'),
      primaryButtonProps: {
        as: 'a',
        href: 'https://www.wix.com/contact',
        target: '_blank',
      } as ButtonProps,
      children: <Text widows>{t('cairo.export.serverErrorMessage')}</Text>,
      ...renderError?.(applicationErrorData),
    };
  }

  return {};
}
